import React, { useEffect, useMemo, useState } from "react";

const ROTATE_MS = 6000;

const PHONE = 640;
// Dishes per page: phones show compact rows, bigger screens cards
const pageSizeFor = (width) => (width < PHONE ? 5 : width < 1024 ? 6 : 10);

const dishKey = (item) => String(item.productId || item.name).toLowerCase();

// Adds up every dish across the active tickets:
//   toStart  - on tickets still waiting (New)
//   cooking  - on started tickets, not ticked off yet
//   ready    - ticked off, or on a Ready ticket (not served yet)
export const summariseDishes = (tickets) => {
  const dishes = new Map();
  for (const ticket of tickets) {
    if (!["new", "preparing", "ready"].includes(ticket.status)) continue;
    for (const item of ticket.items) {
      if (item.status === "cancelled") continue;
      const key = dishKey(item);
      let dish = dishes.get(key);
      if (!dish) {
        dish = { key, name: item.name, toStart: 0, cooking: 0, ready: 0, extras: new Map(), notes: 0 };
        dishes.set(key, dish);
      }
      const qty = Number(item.quantity) || 1;
      if (ticket.status === "ready" || item.status === "done") dish.ready += qty;
      else if (ticket.status === "preparing") dish.cooking += qty;
      else dish.toStart += qty;

      // Variations the chef must not miss when cooking in a batch
      if (ticket.status !== "ready" && item.status !== "done") {
        if (item.addOns?.length) {
          const extra = `+ ${item.addOns.join(", ")}`;
          dish.extras.set(extra, (dish.extras.get(extra) || 0) + qty);
        }
        if (item.note) dish.notes += qty;
      }
    }
  }
  return [...dishes.values()]
    .map((d) => ({ ...d, extras: [...d.extras.entries()] }))
    .sort((a, b) => b.toStart - a.toStart || b.cooking - a.cooking || b.ready - a.ready || a.name.localeCompare(b.name));
};

// Phone row: small number pills, labels in the header line
const Pill = ({ value, className }) => (
  <span
    className={`w-9 shrink-0 rounded-md py-1 text-center text-base font-extrabold leading-none ${
      value ? className : "bg-gray-50 text-gray-300"
    }`}
  >
    {value}
  </span>
);

const extrasText = (dish) =>
  [
    ...dish.extras.slice(0, 2).map(([extra, qty]) => `${qty}× ${extra}`),
    dish.extras.length > 2 ? `+${dish.extras.length - 2} more` : null,
  ]
    .filter(Boolean)
    .join(" · ");

const Count = ({ value, label, className }) => (
  <div className={`flex-1 min-w-0 rounded-md px-1 py-1 text-center ${value ? className : "bg-gray-50 text-gray-300"}`}>
    <div className="text-lg font-extrabold leading-none">{value}</div>
    <div className="text-[10px] font-semibold uppercase mt-0.5 truncate">{label}</div>
  </div>
);

const KitchenSummary = ({ tickets, selectedDish, onSelectDish }) => {
  const dishes = useMemo(() => summariseDishes(tickets), [tickets]);
  const [pageSize, setPageSize] = useState(() => pageSizeFor(window.innerWidth));
  const [isPhone, setIsPhone] = useState(() => window.innerWidth < PHONE);
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem("kitchenSummaryOpen") !== "false";
    } catch {
      return true;
    }
  });

  const pages = Math.max(1, Math.ceil(dishes.length / pageSize));
  const current = Math.min(page, pages - 1);

  useEffect(() => {
    const onResize = () => {
      setPageSize(pageSizeFor(window.innerWidth));
      setIsPhone(window.innerWidth < PHONE);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Rotate through the pages; hold still while someone is touching it
  useEffect(() => {
    if (!open || paused || pages < 2) return undefined;
    const id = setInterval(() => setPage((p) => (p + 1) % pages), ROTATE_MS);
    return () => clearInterval(id);
  }, [open, paused, pages]);

  const toggleOpen = () => {
    setOpen((o) => {
      try {
        localStorage.setItem("kitchenSummaryOpen", String(!o));
      } catch {
        // preference just isn't remembered
      }
      return !o;
    });
  };

  const totals = dishes.reduce(
    (t, d) => ({ toStart: t.toStart + d.toStart, cooking: t.cooking + d.cooking, ready: t.ready + d.ready }),
    { toStart: 0, cooking: 0, ready: 0 },
  );
  const visible = dishes.slice(current * pageSize, current * pageSize + pageSize);

  return (
    <div
      className="bg-white rounded-lg shadow-sm mb-3"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setTimeout(() => setPaused(false), ROTATE_MS)}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-2">
        <button
          type="button"
          onClick={toggleOpen}
          className="flex items-center gap-2 font-bold text-gray-900 text-sm whitespace-nowrap"
        >
          <span className={`transition-transform ${open ? "rotate-90" : ""}`}>▶</span>
          Dish summary
        </button>
        <span className="text-xs text-gray-500">
          {dishes.length} dish{dishes.length === 1 ? "" : "es"} ·{" "}
          <span className="text-amber-700 font-semibold">{totals.toStart} to start</span> ·{" "}
          <span className="text-blue-700 font-semibold">{totals.cooking} cooking</span> ·{" "}
          <span className="text-green-700 font-semibold">{totals.ready} ready</span>
        </span>
        {open && pages > 1 && (
          <div className="ml-auto flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((current - 1 + pages) % pages)}
              className="w-8 h-8 rounded-full border border-gray-200 text-gray-600"
              aria-label="Previous dishes"
            >
              ‹
            </button>
            {Array.from({ length: pages }, (_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setPage(i)}
                className={`w-2 h-2 rounded-full ${i === current ? "bg-gray-800" : "bg-gray-300"}`}
                aria-label={`Dishes page ${i + 1}`}
              />
            ))}
            <button
              type="button"
              onClick={() => setPage((current + 1) % pages)}
              className="w-8 h-8 rounded-full border border-gray-200 text-gray-600"
              aria-label="Next dishes"
            >
              ›
            </button>
          </div>
        )}
      </div>

      {open && (
        <div className="px-3 pb-3">
          {dishes.length === 0 ? (
            <p className="text-sm text-gray-400 py-2">Nothing to cook right now.</p>
          ) : isPhone ? (
            <div>
              <div className="flex items-center gap-1 px-1 pb-1 text-[10px] font-semibold uppercase text-gray-500">
                <span className="flex-1">Dish</span>
                <span className="w-9 text-center text-amber-700">Start</span>
                <span className="w-9 text-center text-blue-700">Cook</span>
                <span className="w-9 text-center text-green-700">Ready</span>
              </div>
              <ul className="divide-y divide-gray-100">
                {visible.map((dish) => {
                  const selected = selectedDish === dish.key;
                  const extras = extrasText(dish);
                  return (
                    <li key={dish.key}>
                      <button
                        type="button"
                        onClick={() => onSelectDish(selected ? null : dish.key)}
                        className={`w-full flex items-center gap-1 px-1 py-2 text-left rounded-md ${
                          selected ? "bg-blue-50 ring-2 ring-blue-600" : ""
                        }`}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="flex items-baseline gap-1.5">
                            <span className="text-lg font-extrabold text-gray-900">{dish.toStart + dish.cooking}</span>
                            <span className="font-bold text-gray-900 truncate">{dish.name}</span>
                          </span>
                          {(extras || dish.notes > 0) && (
                            <span className="block text-xs truncate">
                              {extras && <span className="text-gray-600">{extras}</span>}
                              {extras && dish.notes > 0 && " · "}
                              {dish.notes > 0 && (
                                <span className="font-semibold text-red-700">📝 {dish.notes} note{dish.notes === 1 ? "" : "s"}</span>
                              )}
                            </span>
                          )}
                        </span>
                        <Pill value={dish.toStart} className="bg-amber-100 text-amber-800" />
                        <Pill value={dish.cooking} className="bg-blue-100 text-blue-800" />
                        <Pill value={dish.ready} className="bg-green-100 text-green-800" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
              {visible.map((dish) => {
                const selected = selectedDish === dish.key;
                return (
                  <button
                    key={dish.key}
                    type="button"
                    onClick={() => onSelectDish(selected ? null : dish.key)}
                    className={`text-left rounded-lg border-2 p-2 transition-colors ${
                      selected ? "border-blue-600 bg-blue-50" : "border-gray-200 hover:border-gray-300"
                    }`}
                    title="Show only tickets with this dish"
                  >
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-xl font-extrabold text-gray-900">{dish.toStart + dish.cooking}</span>
                      <span className="font-bold text-gray-900 truncate">{dish.name}</span>
                    </div>
                    <div className="flex gap-1 mt-1.5">
                      <Count value={dish.toStart} label="To start" className="bg-amber-100 text-amber-800" />
                      <Count value={dish.cooking} label="Cooking" className="bg-blue-100 text-blue-800" />
                      <Count value={dish.ready} label="Ready" className="bg-green-100 text-green-800" />
                    </div>
                    {(dish.extras.length > 0 || dish.notes > 0) && (
                      <div className="mt-1.5 space-y-0.5 text-xs">
                        {dish.extras.slice(0, 2).map(([extra, qty]) => (
                          <div key={extra} className="text-gray-700 truncate">
                            {qty}× {extra}
                          </div>
                        ))}
                        {dish.extras.length > 2 && (
                          <div className="text-gray-500">+{dish.extras.length - 2} more variations</div>
                        )}
                        {dish.notes > 0 && (
                          <div className="font-semibold text-red-700">📝 {dish.notes} with a note</div>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export { dishKey };
export default KitchenSummary;
